import { RootState } from "@/models/RootState.type";
import { AppDispatch } from "@/models/AppDispatch.type";
import { createListenerMiddleware } from "@reduxjs/toolkit";
import {
  fetchResourcesMetadata,
  fetchSkillResources,
  updateLessonResources,
} from "../slices";
import { ResourceType } from "@/enums/ResourceType";
import { AmiraResources } from "@/models/AmiraResources.interface";

const getResourceGroupType = (resourceType: string) => {
  switch (resourceType) {
    case ResourceType.microLesson:
    case ResourceType.istationLegacyActivity:
      return "ISTATION_STORY";
    case ResourceType.teacherResource:
    case ResourceType.EPS:
    case ResourceType.ERSS:
    case ResourceType.skillTutor:
    case ResourceType.textSet:
      return "TEACHER_RESOURCE";
    case ResourceType.tutor:
      return "AMIRA_STORY";
    default:
      return null;
  }
};

export const addFetchResourceQueueListenerMiddleware = (
  listenerMiddleware: ReturnType<typeof createListenerMiddleware>
) => {
  listenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
    actionCreator: fetchSkillResources.fulfilled,
    effect: async (action: any, listenerApi: any) => {
      try {
        const { weeklyPlan, reports } = listenerApi.getState() as RootState;
        if (weeklyPlan.data?.resourcesFetchQueue) {
          if (
            weeklyPlan.data?.resourcesFetchQueue.length <= 1 &&
            weeklyPlan.fetchSkillResourcesLoading === 0
          ) {
            const resourcesMetadataFilter: Map<
              string,
              {
                resourceGroupType: string;
                resourceUniqueId: string;
              }
            > = new Map();
            weeklyPlan.data?.resources.forEach(
              (resource: { skillId: string; resources: AmiraResources[] }) => {
                resource.resources.forEach(resource => {
                  const resourceGroupType = getResourceGroupType(
                    resource.resourceType
                  );
                  if (resourceGroupType) {
                    resourcesMetadataFilter.set(resource.resourceUniqueId, {
                      resourceGroupType,
                      resourceUniqueId: resource.resourceUniqueId,
                    });
                  }
                });
              }
            );
            weeklyPlan.data.curriculum.tracks.forEach(track => {
              track.units.forEach(unit => {
                unit.lessons.forEach(lesson => {
                  const manifests = [
                    ...lesson.mainAssignment.manifest,
                    ...lesson.studentAssignments?.flatMap(a => a.manifest),
                  ];
                  manifests.forEach(manifest => {
                    if ('storyId' in manifest && manifest.storyId) {
                      const resourceGroupType = getResourceGroupType(
                        'activityType' in manifest ? manifest.activityType || "" : ""
                      );
                      if (resourceGroupType) {
                        resourcesMetadataFilter.set(manifest.storyId, {
                          resourceGroupType,
                          resourceUniqueId: manifest.storyId,
                        });
                      }
                    }
                  });
                });
              });
            });
            listenerApi.dispatch(
              fetchResourcesMetadata({
                input: Array.from(resourcesMetadataFilter.values()),
              })
            );
          }
        }
      } catch (error) {
        console.error("Error fetching skill resources in queue", error);
        // listenerApi.dispatch(setLoading(AsyncLoadingState.ERROR));
      }
    },
  });
};

export const updateLessonResourcesMiddleware = (
  listenerMiddleware: ReturnType<typeof createListenerMiddleware>
) => {
  listenerMiddleware.startListening.withTypes<RootState, AppDispatch>()({
    actionCreator: fetchResourcesMetadata.fulfilled,
    effect: async (action: any, listenerApi: any) => {
      try {
        const { weeklyPlan, reports } = listenerApi.getState() as RootState;
        if (weeklyPlan.data && weeklyPlan.data.resources.length > 0) {
          // dispatch action to update lesson resources list
          listenerApi.dispatch(
            updateLessonResources({
              resources: weeklyPlan.data?.resources ?? [],
              students: reports.roster.data.student.map(
                (s: { id: string; grade: string }) => s
              ),
            })
          );
        }
      } catch (error) {
        console.error("Error calling updateLessonResources", error);
        // listenerApi.dispatch(setLoading(AsyncLoadingState.ERROR));
      }
    },
  });
};
